import { useEffect } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  useNavigate,
} from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { Spinner } from '@zt/ui';
import { Shell } from './components/Shell';
import { StepUpDialog } from './components/StepUp';
import { registerAuthLostHandler } from './lib/api';
import { HelpProvider } from './lib/help';
import { useT } from './lib/i18n';
import { useMe, useSession } from './lib/session';
import { AccountPage } from './pages/account';
import { ForgotPage, InvitePage, LoginPage, MfaEnrollPage, MfaPage, ResetPage } from './pages/auth';
import { BrandsPage } from './pages/brands';
import {
  ApplicationsPage,
  CustomerDetailPage,
  CustomersPage,
  DealerDetailPage,
  DealersPage,
} from './pages/customers';
import { ImportsPage } from './pages/imports';
import { InvoicesPage } from './pages/invoices';
import { LegalTextsPage } from './pages/legal';
import { BrokenLinksPage, RedirectsPage } from './pages/redirects';
import { DesignPage } from './pages/design';
import { MenusPage } from './pages/menus';
import { PageDesignsPage } from './pages/page-designs';
import { SalesPointsPage } from './pages/sales-points';
import { MigrationPage } from './pages/migration';
import { PrivacyPage } from './pages/privacy';
import { RetentionPage } from './pages/retention';
import { CompanyLedgerPage, LedgerOverviewPage } from './pages/ledger';
import { BankImportPage } from './pages/bank-import';
import { QuoteDetailPage, QuotesPage } from './pages/quotes';
import { RegionsPage } from './pages/regions';
import { CommissionsPage } from './pages/commissions';
import { DealerReportsPage } from './pages/dealer-reports';
import { InternationalReportPage } from './pages/international-report';
import { SubscriptionsPage } from './pages/subscriptions';
import { SalesReportPage } from './pages/sales-report';
import { FinanceInputsPage } from './pages/finance-inputs';
import { FinanceReportPage } from './pages/finance-report';
import { StockReportPage } from './pages/stock-report';
import { SupplierDetailPage, SuppliersPage } from './pages/suppliers';
import { PurchaseOrderDetailPage, PurchaseOrderNewPage, PurchaseOrdersPage } from './pages/purchase-orders';
import { GoodsReceiptPage } from './pages/goods-receipt';
import { ReportBuilderPage } from './pages/report-builder';
import { ReportFilesPage } from './pages/report-files';
import { PaymentDisputeDetailPage, PaymentDisputesPage } from './pages/payment-disputes';
import { WorkflowDetailPage, WorkflowRunPage, WorkflowsPage } from './pages/workflows';
import { PriceSimulationPage } from './pages/price-simulation';
import { MarketingSummaryPage } from './pages/marketing-summary';
import { MultiTouchPage } from './pages/multi-touch';
import { BizimhesapPage } from './pages/bizimhesap';
import { ExportsPage } from './pages/exports';
import { SeoPage } from './pages/seo';
import { TrackingPage } from './pages/tracking';
import { GooglePage } from './pages/google';
import { SearchConsolePage } from './pages/search-console';
import { MerchantPage } from './pages/merchant';
import { AttributionPage } from './pages/attribution';
import { WebVitalsPage } from './pages/web-vitals';
import { KeywordsPage } from './pages/keywords';
import { SiteSearchPage } from './pages/site-search';
import { AttributesPage } from './pages/attributes';
import { CollectionEditorPage, CollectionsPage } from './pages/collections';
import { PageEditorPage, PagesPage } from './pages/content-pages';
import { BannerEditorPage, BannersPage } from './pages/banners';
import { BlogPage, PostEditorPage } from './pages/blog';
import { ContentCalendarPage, ContentReportPage } from './pages/content-plan';
import { ContactMessagesPage, FormsPage } from './pages/forms';
import { MarketingAudiencePage } from './pages/marketing-audience';
import { SegmentEditorPage, SegmentsPage } from './pages/segments';
import { EmailTemplateEditorPage, EmailTemplatesPage } from './pages/email-templates';
import { AutomationEditorPage, AutomationsPage } from './pages/automations';
import { ProductAlertsPage } from './pages/product-alerts';
import {
  MessageCampaignEditorPage,
  MessageCampaignsPage,
  SmsTemplateEditorPage,
  SmsTemplatesPage,
} from './pages/messaging';
import { PushCampaignEditorPage, PushPage } from './pages/push';
import { PromotionEditorPage, PromotionsPage } from './pages/promotions';
import { GiftCardDetailPage, GiftCardsPage } from './pages/gift-cards';
import { LoyaltyPage } from './pages/loyalty';
import { ReferralsPage } from './pages/referrals';
import { AffiliatesPage } from './pages/affiliates';
import { SupportTicketPage, SupportTicketsPage } from './pages/support';
import { LiveChatPage } from './pages/chat';
import { AdsPage } from './pages/ads';
import { GoogleAdsCampaignPage, GoogleAdsCampaignsPage } from './pages/google-ads';
import { NeedsFinderPage } from './pages/needs-finder';
import { GoogleReportsPage } from './pages/google-reports';
import { CampaignEditorPage, CampaignsPage } from './pages/campaigns';
import { QuestionDetailPage, QuestionsPage } from './pages/questions';
import { ReviewDetailPage, ReviewsPage } from './pages/reviews';
import { OrderDetailPage, OrdersPage } from './pages/orders';
import { OrderPrintPage, printSearch } from './pages/order-tools';
import { OrderProformaPage } from './pages/proforma';
import { PaymentSettingsPage, PaymentsPage } from './pages/payments';
import { ShippingMethodsPage } from './pages/shipping';
import { DiscountsPage, PricingPage } from './pages/pricing';
import { FxPage } from './pages/fx';
import { AiSettingsPage } from './pages/ai/settings';
import { RecommendationsPage } from './pages/ai/recommendations';
import { ForecastPage } from './pages/ai/forecast';
import { ContentStudioPage, ContentStudioProjectPage } from './pages/ai/studio';
import { CopilotPage } from './pages/ai/copilot';
import { SeoSuggestionsPage } from './pages/ai/seo';
import { LtvPage } from './pages/ai/ltv';
import { YoutubePage } from './pages/ai/youtube';
import { ProductEditorPage, ProductsPage } from './pages/products';
import { ProductBulkPage } from './pages/product-bulk';
import { ProductGridPage } from './pages/product-grid';
import { CategoriesPage, StockPage } from './pages/stock';
import { ApiKeysPage, IntegrationsPage, WebhooksPage } from './pages/connections';
import { DashboardPage, SetupPage } from './pages/dashboard';
import { OrganizationPage } from './pages/organization';
import { AuditPageView, HelpEditorPage, SystemPage } from './pages/records';
import { UiTranslationsPage } from './pages/ui-translations';
import { StoreDetailPage, StoresPage } from './pages/stores';
import { RolesPage, UsersPage } from './pages/team';

function Root() {
  const session = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  useEffect(() => {
    registerAuthLostHandler(() => {
      void qc.invalidateQueries({ queryKey: ['session'] });
    });
    return () => registerAuthLostHandler(null);
  }, [qc, navigate]);
  return (
    <HelpProvider
      signedIn={Boolean(session.data?.session.mfaVerified)}
      uiMode={session.data?.user.uiMode ?? 'beginner'}
    >
      <Outlet />
      <StepUpDialog />
      {/* richColors kapalı: varsayılan tema WCAG AA kontrastını sağlar */}
      <Toaster
        position="top-center"
        closeButton
        toastOptions={{ style: { fontFamily: 'var(--zt-font-sans)' } }}
      />
    </HelpProvider>
  );
}

/** Oturum ve MFA durumuna göre koruyan düzen. */
function Protected() {
  const t = useT();
  const session = useSession();
  const verified = Boolean(session.data?.session.mfaVerified);
  const me = useMe(verified);
  if (session.isLoading) return <Spinner label={t('common.loading')} />;
  if (!session.data) return <Navigate to="/giris" />;
  if (!verified) return <Navigate to={session.data.mfaEnrolled ? '/mfa' : '/mfa-kurulum'} />;
  if (me.isLoading) return <Spinner label={t('common.loading')} />;
  return (
    <Shell>
      <Outlet />
    </Shell>
  );
}

const tokenSearch = (s: Record<string, unknown>) => ({ token: typeof s.token === 'string' ? s.token : '' });

const root = createRootRoute({ component: Root });
const login = createRoute({ getParentRoute: () => root, path: '/giris', component: LoginPage });
const mfa = createRoute({ getParentRoute: () => root, path: '/mfa', component: MfaPage });
const mfaEnroll = createRoute({ getParentRoute: () => root, path: '/mfa-kurulum', component: MfaEnrollPage });
const forgot = createRoute({ getParentRoute: () => root, path: '/sifremi-unuttum', component: ForgotPage });
const reset = createRoute({
  getParentRoute: () => root,
  path: '/sifre-sifirla',
  component: ResetPage,
  validateSearch: tokenSearch,
});
const invite = createRoute({
  getParentRoute: () => root,
  path: '/davet',
  component: InvitePage,
  validateSearch: tokenSearch,
});

const app = createRoute({ getParentRoute: () => root, id: 'app', component: Protected });
// Faz 9 (yapay zekâ)
const aiSettingsRoute = createRoute({
  getParentRoute: () => app,
  path: '/yapay-zeka/ayarlar',
  component: AiSettingsPage,
});
const aiRecommendationsRoute = createRoute({
  getParentRoute: () => app,
  path: '/yapay-zeka/oneriler',
  component: RecommendationsPage,
});
const aiForecastRoute = createRoute({
  getParentRoute: () => app,
  path: '/yapay-zeka/talep-tahmini',
  component: ForecastPage,
});
const aiCopilotRoute = createRoute({
  getParentRoute: () => app,
  path: '/yapay-zeka/copilot',
  component: CopilotPage,
});
const aiSeoRoute = createRoute({
  getParentRoute: () => app,
  path: '/yapay-zeka/seo-onerileri',
  component: SeoSuggestionsPage,
});
const aiLtvRoute = createRoute({
  getParentRoute: () => app,
  path: '/yapay-zeka/musteri-degeri',
  component: LtvPage,
});
const aiYoutubeRoute = createRoute({
  getParentRoute: () => app,
  path: '/yapay-zeka/youtube',
  component: YoutubePage,
});
const aiStudioRoute = createRoute({
  getParentRoute: () => app,
  path: '/yapay-zeka/icerik-studyosu',
  component: ContentStudioPage,
});
const aiStudioProjectRoute = createRoute({
  getParentRoute: () => app,
  path: '/yapay-zeka/icerik-studyosu/$id',
  component: ContentStudioProjectPage,
});
const r0 = createRoute({ getParentRoute: () => app, path: '/', component: DashboardPage });
const r1 = createRoute({ getParentRoute: () => app, path: '/kurulum', component: SetupPage });
const r2 = createRoute({ getParentRoute: () => app, path: '/isletme', component: OrganizationPage });
const r3 = createRoute({ getParentRoute: () => app, path: '/markalar', component: BrandsPage });
const r4 = createRoute({ getParentRoute: () => app, path: '/magazalar', component: StoresPage });
const r5 = createRoute({ getParentRoute: () => app, path: '/magazalar/$id', component: StoreDetailPage });
const r6 = createRoute({ getParentRoute: () => app, path: '/kullanicilar', component: UsersPage });
const r7 = createRoute({ getParentRoute: () => app, path: '/roller', component: RolesPage });
const r8 = createRoute({ getParentRoute: () => app, path: '/entegrasyonlar', component: IntegrationsPage });
const r9 = createRoute({ getParentRoute: () => app, path: '/api-anahtarlari', component: ApiKeysPage });
const r10 = createRoute({ getParentRoute: () => app, path: '/webhooklar', component: WebhooksPage });
const r11 = createRoute({ getParentRoute: () => app, path: '/denetim', component: AuditPageView });
const r12 = createRoute({ getParentRoute: () => app, path: '/sistem', component: SystemPage });
const r13 = createRoute({ getParentRoute: () => app, path: '/yardim-metinleri', component: HelpEditorPage });
const r15 = createRoute({ getParentRoute: () => app, path: '/ceviriler', component: UiTranslationsPage });
const r14 = createRoute({ getParentRoute: () => app, path: '/hesabim', component: AccountPage });
const c1 = createRoute({ getParentRoute: () => app, path: '/urunler', component: ProductsPage });
const c2 = createRoute({ getParentRoute: () => app, path: '/urunler/$id', component: ProductEditorPage });
const c18 = createRoute({ getParentRoute: () => app, path: '/toplu-islemler', component: ProductBulkPage });
const c19 = createRoute({ getParentRoute: () => app, path: '/urun-tablosu', component: ProductGridPage });
const c3 = createRoute({ getParentRoute: () => app, path: '/kategoriler', component: CategoriesPage });
const c15 = createRoute({ getParentRoute: () => app, path: '/urun-ozellikleri', component: AttributesPage });
const c16 = createRoute({ getParentRoute: () => app, path: '/koleksiyonlar', component: CollectionsPage });
const c17 = createRoute({
  getParentRoute: () => app,
  path: '/koleksiyonlar/$id',
  component: CollectionEditorPage,
});
const c4 = createRoute({ getParentRoute: () => app, path: '/fiyatlar', component: PricingPage });
const fxRoute = createRoute({ getParentRoute: () => app, path: '/doviz-kurlari', component: FxPage });
const c5 = createRoute({ getParentRoute: () => app, path: '/kuponlar', component: DiscountsPage });
const c5p = createRoute({
  getParentRoute: () => app,
  path: '/otomatik-indirimler',
  component: PromotionsPage,
});
const c5pe = createRoute({
  getParentRoute: () => app,
  path: '/otomatik-indirimler/$id',
  component: PromotionEditorPage,
});
const c5g = createRoute({ getParentRoute: () => app, path: '/hediye-kartlari', component: GiftCardsPage });
const c5gd = createRoute({
  getParentRoute: () => app,
  path: '/hediye-kartlari/$id',
  component: GiftCardDetailPage,
});
const mkLoyalty = createRoute({ getParentRoute: () => app, path: '/sadakat', component: LoyaltyPage });
const mkReferrals = createRoute({
  getParentRoute: () => app,
  path: '/tavsiye-programi',
  component: ReferralsPage,
});
const mkAffiliates = createRoute({
  getParentRoute: () => app,
  path: '/is-ortaklari',
  component: AffiliatesPage,
});
const c6 = createRoute({ getParentRoute: () => app, path: '/stok', component: StockPage });
const c7 = createRoute({ getParentRoute: () => app, path: '/musteriler', component: CustomersPage });
const c8 = createRoute({ getParentRoute: () => app, path: '/musteriler/$id', component: CustomerDetailPage });
const c9 = createRoute({ getParentRoute: () => app, path: '/bayiler', component: DealersPage });
const c10 = createRoute({ getParentRoute: () => app, path: '/bayiler/$id', component: DealerDetailPage });
const salesPointsRoute = createRoute({
  getParentRoute: () => app,
  path: '/satis-noktalari',
  component: SalesPointsPage,
});
const ledgerList = createRoute({
  getParentRoute: () => app,
  path: '/cari-hesaplar',
  component: LedgerOverviewPage,
});
const ledgerBankImport = createRoute({
  getParentRoute: () => app,
  path: '/cari-hesaplar/banka-hareketleri',
  component: BankImportPage,
});
const ledgerDetail = createRoute({
  getParentRoute: () => app,
  path: '/cari-hesaplar/$id',
  component: CompanyLedgerPage,
});
const quoteList = createRoute({ getParentRoute: () => app, path: '/teklifler', component: QuotesPage });
const salesRegions = createRoute({ getParentRoute: () => app, path: '/bolgeler', component: RegionsPage });
const bizimhesapRoute = createRoute({
  getParentRoute: () => app,
  path: '/bizimhesap',
  component: BizimhesapPage,
});
const commissionsRoute = createRoute({
  getParentRoute: () => app,
  path: '/komisyonlar',
  component: CommissionsPage,
});
const dealerReportsRoute = createRoute({
  getParentRoute: () => app,
  path: '/bayi-raporlari',
  component: DealerReportsPage,
});
const internationalReportRoute = createRoute({
  getParentRoute: () => app,
  path: '/ulke-raporu',
  component: InternationalReportPage,
});
const subscriptionsRoute = createRoute({
  getParentRoute: () => app,
  path: '/duzenli-siparisler',
  component: SubscriptionsPage,
});
// Faz 8 raporları (ADR-801: ayrı blok).
const salesReportRoute = createRoute({
  getParentRoute: () => app,
  path: '/satis-raporu',
  component: SalesReportPage,
});
const financeInputsRoute = createRoute({
  getParentRoute: () => app,
  path: '/finans-girdileri',
  component: FinanceInputsPage,
});
const financeReportRoute = createRoute({
  getParentRoute: () => app,
  path: '/finans-raporu',
  component: FinanceReportPage,
});
const stockReportRoute = createRoute({
  getParentRoute: () => app,
  path: '/stok-raporu',
  component: StockReportPage,
});
const suppliersRoute = createRoute({
  getParentRoute: () => app,
  path: '/tedarikciler',
  component: SuppliersPage,
});
const supplierRoute = createRoute({
  getParentRoute: () => app,
  path: '/tedarikciler/$id',
  component: SupplierDetailPage,
});
const purchaseOrdersRoute = createRoute({
  getParentRoute: () => app,
  path: '/satin-alma',
  component: PurchaseOrdersPage,
});
const purchaseOrderNewRoute = createRoute({
  getParentRoute: () => app,
  path: '/satin-alma/yeni',
  component: PurchaseOrderNewPage,
});
const purchaseOrderRoute = createRoute({
  getParentRoute: () => app,
  path: '/satin-alma/$id',
  component: PurchaseOrderDetailPage,
});
const reportBuilderRoute = createRoute({
  getParentRoute: () => app,
  path: '/rapor-olusturucu',
  component: ReportBuilderPage,
  // Ana panel bileşeninden açılan kayıtlı rapor (F8-09).
  validateSearch: (s: Record<string, unknown>): { rapor?: string } =>
    typeof s.rapor === 'string' ? { rapor: s.rapor } : {},
});
const goodsReceiptRoute = createRoute({
  getParentRoute: () => app,
  path: '/mal-kabul',
  component: GoodsReceiptPage,
});
const multiTouchRoute = createRoute({
  getParentRoute: () => app,
  path: '/coklu-dokunus',
  component: MultiTouchPage,
});
const marketingSummaryRoute = createRoute({
  getParentRoute: () => app,
  path: '/pazarlama-ozeti',
  component: MarketingSummaryPage,
});
const priceSimulationRoute = createRoute({
  getParentRoute: () => app,
  path: '/fiyat-simulasyonu',
  component: PriceSimulationPage,
});
const disputesRoute = createRoute({
  getParentRoute: () => app,
  path: '/odeme-itirazlari',
  component: PaymentDisputesPage,
});
const disputeRoute = createRoute({
  getParentRoute: () => app,
  path: '/odeme-itirazlari/$id',
  component: PaymentDisputeDetailPage,
});
const reportFilesRoute = createRoute({
  getParentRoute: () => app,
  path: '/rapor-dosyalari',
  component: ReportFilesPage,
  // Rapor oluşturucudan "Zamanla" ile gelinirse zamanlama penceresi bu raporla açılır (F8-10).
  validateSearch: (s: Record<string, unknown>): { zamanla?: string } =>
    typeof s.zamanla === 'string' ? { zamanla: s.zamanla } : {},
});
const workflowsRoute = createRoute({
  getParentRoute: () => app,
  path: '/otomasyon-akislari',
  component: WorkflowsPage,
});
const workflowRoute = createRoute({
  getParentRoute: () => app,
  path: '/otomasyon-akislari/$id',
  component: WorkflowDetailPage,
});
const workflowRunRoute = createRoute({
  getParentRoute: () => app,
  path: '/otomasyon-calismasi/$id',
  component: WorkflowRunPage,
});
const quoteDetail = createRoute({
  getParentRoute: () => app,
  path: '/teklifler/$id',
  component: QuoteDetailPage,
});
const c11 = createRoute({
  getParentRoute: () => app,
  path: '/bayilik-basvurulari',
  component: ApplicationsPage,
});
const c12 = createRoute({ getParentRoute: () => app, path: '/siparisler', component: OrdersPage });
const c13 = createRoute({ getParentRoute: () => app, path: '/siparisler/$id', component: OrderDetailPage });
const c21 = createRoute({
  getParentRoute: () => app,
  path: '/siparisler/$id/proforma',
  component: OrderProformaPage,
});
const c20 = createRoute({
  getParentRoute: () => app,
  path: '/siparis-yazdir',
  component: OrderPrintPage,
  validateSearch: printSearch,
});
const c14 = createRoute({ getParentRoute: () => app, path: '/ice-aktarma', component: ImportsPage });
const p1 = createRoute({
  getParentRoute: () => app,
  path: '/odeme-ayarlari',
  component: PaymentSettingsPage,
});
const p2 = createRoute({ getParentRoute: () => app, path: '/odemeler', component: PaymentsPage });
const p3 = createRoute({ getParentRoute: () => app, path: '/faturalar', component: InvoicesPage });
const p4 = createRoute({ getParentRoute: () => app, path: '/kargo', component: ShippingMethodsPage });
const p5 = createRoute({ getParentRoute: () => app, path: '/yasal-metinler', component: LegalTextsPage });
const p6 = createRoute({ getParentRoute: () => app, path: '/yonlendirmeler', component: RedirectsPage });
const brokenLinksRoute = createRoute({
  getParentRoute: () => app,
  path: '/kirik-linkler',
  component: BrokenLinksPage,
});
const d1 = createRoute({ getParentRoute: () => app, path: '/tasarim', component: DesignPage });
const d2 = createRoute({ getParentRoute: () => app, path: '/menuler', component: MenusPage });
const dPageDesigns = createRoute({
  getParentRoute: () => app,
  path: '/sayfa-tasarimlari',
  component: PageDesignsPage,
});
const d3 = createRoute({ getParentRoute: () => app, path: '/sayfalar', component: PagesPage });
const d4 = createRoute({ getParentRoute: () => app, path: '/sayfalar/$id', component: PageEditorPage });
const d5 = createRoute({ getParentRoute: () => app, path: '/bannerlar', component: BannersPage });
const d6 = createRoute({ getParentRoute: () => app, path: '/bannerlar/$id', component: BannerEditorPage });
const d7 = createRoute({ getParentRoute: () => app, path: '/blog', component: BlogPage });
const d8 = createRoute({ getParentRoute: () => app, path: '/blog/$id', component: PostEditorPage });
// Evcil Dostumun İhtiyacı (F7-39).
const needsFinderRoute = createRoute({
  getParentRoute: () => app,
  path: '/ihtiyac-bulucu',
  component: NeedsFinderPage,
});
const d13 = createRoute({
  getParentRoute: () => app,
  path: '/icerik-takvimi',
  component: ContentCalendarPage,
});
const d14 = createRoute({ getParentRoute: () => app, path: '/icerik-raporu', component: ContentReportPage });
const d15 = createRoute({ getParentRoute: () => app, path: '/formlar', component: FormsPage });
const d16 = createRoute({
  getParentRoute: () => app,
  path: '/iletisim-mesajlari',
  component: ContactMessagesPage,
});
const d16s = createRoute({
  getParentRoute: () => app,
  path: '/destek-talepleri',
  component: SupportTicketsPage,
});
const d16c = createRoute({ getParentRoute: () => app, path: '/canli-sohbet', component: LiveChatPage });
const d17a = createRoute({ getParentRoute: () => app, path: '/reklam-merkezi', component: AdsPage });
// Google Ads kampanyaları (F7-38).
const gadsRoute = createRoute({
  getParentRoute: () => app,
  path: '/reklam-merkezi/google-ads',
  component: GoogleAdsCampaignsPage,
});
const gadsCampaignRoute = createRoute({
  getParentRoute: () => app,
  path: '/reklam-merkezi/google-ads/$id',
  component: function GoogleAdsCampaignRoute() {
    const { id } = gadsCampaignRoute.useParams();
    return <GoogleAdsCampaignPage id={id} />;
  },
});
const d18r = createRoute({
  getParentRoute: () => app,
  path: '/google-raporlari',
  component: GoogleReportsPage,
});
const d16t = createRoute({
  getParentRoute: () => app,
  path: '/destek-talepleri/$id',
  component: SupportTicketPage,
});
const mkAudience = createRoute({
  getParentRoute: () => app,
  path: '/pazarlama-izinleri',
  component: MarketingAudiencePage,
});
const mkSegments = createRoute({ getParentRoute: () => app, path: '/segmentler', component: SegmentsPage });
const mkSegment = createRoute({
  getParentRoute: () => app,
  path: '/segmentler/$id',
  component: SegmentEditorPage,
});
const mkCampaigns = createRoute({
  getParentRoute: () => app,
  path: '/e-posta-kampanyalari',
  component: CampaignsPage,
});
const mkCampaign = createRoute({
  getParentRoute: () => app,
  path: '/e-posta-kampanyalari/$id',
  component: CampaignEditorPage,
});
const mkAutomations = createRoute({
  getParentRoute: () => app,
  path: '/otomasyonlar',
  component: AutomationsPage,
});
const mkAutomation = createRoute({
  getParentRoute: () => app,
  path: '/otomasyonlar/$kind',
  component: AutomationEditorPage,
});
const mkProductAlerts = createRoute({
  getParentRoute: () => app,
  path: '/stok-ve-fiyat-bildirimleri',
  component: ProductAlertsPage,
});
const mkMessages = createRoute({
  getParentRoute: () => app,
  path: '/sms-whatsapp-kampanyalari',
  component: MessageCampaignsPage,
});
const mkMessage = createRoute({
  getParentRoute: () => app,
  path: '/sms-whatsapp-kampanyalari/$id',
  component: MessageCampaignEditorPage,
});
const mkSmsTemplates = createRoute({
  getParentRoute: () => app,
  path: '/sms-sablonlari',
  component: SmsTemplatesPage,
});
const mkSmsTemplate = createRoute({
  getParentRoute: () => app,
  path: '/sms-sablonlari/$id',
  component: SmsTemplateEditorPage,
});
const mkPush = createRoute({ getParentRoute: () => app, path: '/anlik-bildirimler', component: PushPage });
const mkPushCampaign = createRoute({
  getParentRoute: () => app,
  path: '/anlik-bildirimler/$id',
  component: PushCampaignEditorPage,
});
const mkTemplates = createRoute({
  getParentRoute: () => app,
  path: '/e-posta-sablonlari',
  component: EmailTemplatesPage,
});
const mkTemplate = createRoute({
  getParentRoute: () => app,
  path: '/e-posta-sablonlari/$id',
  component: EmailTemplateEditorPage,
});
const d9 = createRoute({ getParentRoute: () => app, path: '/sorular', component: QuestionsPage });
const d10 = createRoute({ getParentRoute: () => app, path: '/sorular/$id', component: QuestionDetailPage });
const d11 = createRoute({
  getParentRoute: () => app,
  path: '/urun-degerlendirmeleri',
  component: ReviewsPage,
});
const d12 = createRoute({
  getParentRoute: () => app,
  path: '/urun-degerlendirmeleri/$id',
  component: ReviewDetailPage,
});
const m1 = createRoute({ getParentRoute: () => app, path: '/tasima-paketi', component: MigrationPage });
const k1 = createRoute({ getParentRoute: () => app, path: '/kvkk-basvurulari', component: PrivacyPage });
const k2 = createRoute({ getParentRoute: () => app, path: '/saklama-imha', component: RetentionPage });
const x1 = createRoute({ getParentRoute: () => app, path: '/disa-aktarma', component: ExportsPage });
const s1 = createRoute({ getParentRoute: () => app, path: '/seo', component: SeoPage });
const s2 = createRoute({ getParentRoute: () => app, path: '/olcumleme', component: TrackingPage });
const s3 = createRoute({ getParentRoute: () => app, path: '/kaynaklar', component: AttributionPage });
const s4 = createRoute({ getParentRoute: () => app, path: '/sayfa-hizi', component: WebVitalsPage });
const s5 = createRoute({ getParentRoute: () => app, path: '/anahtar-kelimeler', component: KeywordsPage });
const s6 = createRoute({ getParentRoute: () => app, path: '/site-ici-arama', component: SiteSearchPage });
const g1 = createRoute({ getParentRoute: () => app, path: '/google', component: GooglePage });
const g3 = createRoute({ getParentRoute: () => app, path: '/google-alisveris', component: MerchantPage });
const g2 = createRoute({
  getParentRoute: () => app,
  path: '/arama-performansi',
  component: SearchConsolePage,
});

const routeTree = root.addChildren([
  login,
  mfa,
  mfaEnroll,
  forgot,
  reset,
  invite,
  app.addChildren([
    aiSettingsRoute,
    aiRecommendationsRoute,
    aiForecastRoute,
    aiStudioRoute,
    aiStudioProjectRoute,
    aiCopilotRoute,
    aiSeoRoute,
    aiLtvRoute,
    aiYoutubeRoute,
    r0,
    salesReportRoute,
    financeInputsRoute,
    financeReportRoute,
    stockReportRoute,
    suppliersRoute,
    supplierRoute,
    purchaseOrdersRoute,
    purchaseOrderNewRoute,
    purchaseOrderRoute,
    goodsReceiptRoute,
    reportBuilderRoute,
    reportFilesRoute,
    disputesRoute,
    disputeRoute,
    priceSimulationRoute,
    marketingSummaryRoute,
    multiTouchRoute,
    workflowsRoute,
    workflowRoute,
    workflowRunRoute,
    r1,
    r2,
    r3,
    r4,
    r5,
    r6,
    r7,
    r8,
    r9,
    r10,
    r11,
    r12,
    r13,
    r15,
    r14,
    c1,
    c18,
    c19,
    c2,
    c3,
    c4,
    fxRoute,
    c5,
    c6,
    c7,
    c8,
    c9,
    c10,
    salesPointsRoute,
    ledgerList,
    ledgerBankImport,
    ledgerDetail,
    quoteList,
    quoteDetail,
    salesRegions,
    commissionsRoute,
    dealerReportsRoute,
    internationalReportRoute,
    subscriptionsRoute,
    bizimhesapRoute,
    c11,
    c12,
    c13,
    c20,
    c21,
    c14,
    c15,
    c16,
    c17,
    p1,
    p2,
    p3,
    p4,
    p5,
    p6,
    brokenLinksRoute,
    d1,
    d2,
    dPageDesigns,
    d3,
    d4,
    d5,
    d6,
    d7,
    d8,
    d9,
    d10,
    d11,
    d12,
    d13,
    d14,
    d15,
    d16,
    d16s,
    d16t,
    d16c,
    d17a,
    gadsRoute,
    gadsCampaignRoute,
    needsFinderRoute,
    d18r,
    mkAudience,
    mkSegments,
    mkSegment,
    mkTemplates,
    mkTemplate,
    mkCampaigns,
    mkCampaign,
    mkAutomations,
    mkAutomation,
    mkProductAlerts,
    mkMessages,
    mkMessage,
    mkSmsTemplates,
    mkSmsTemplate,
    mkPush,
    c5p,
    c5pe,
    c5g,
    c5gd,
    mkLoyalty,
    mkReferrals,
    mkAffiliates,
    mkPushCampaign,
    m1,
    k1,
    k2,
    x1,
    s1,
    s2,
    s3,
    s4,
    s5,
    s6,
    g1,
    g2,
    g3,
  ]),
]);

export const router = createRouter({ routeTree, defaultPreload: false });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
